import { hasError, type Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { SignInCancelled, type SignInFailed, signInWithApple, signInWithGoogle } from "../../core/ops/auth-ops";
import { CLAIM_TOKEN_PATTERN, claimMessage, previewClaim, redeemClaim } from "../../core/ops/shop-claim-ops";
import { thrownText } from "../../core/primitives/errors";
import { ShopClaimActions, ShopClaimState } from "./shop-claim-model";

export type ShopClaimDeps = Pick<
  AppDeps,
  "client" | "session" | "authHandler" | "profileRepository" | "storeRepository"
>;

export interface ShopClaimParams {
  readonly token: string | null;
}

export interface ShopClaimRouting {
  /** Leaves the claim: to the app when signed in as a merchant, to the login form otherwise. */
  close(registered: boolean): void;
  goToLogin(): void;
  toClaimedShop(storeId: string): void;
}

const INCOMPLETE_LINK = "This claim link is incomplete. Reopen the invitation email to try again.";
const PREVIEW_FAILED = "Could not check this invitation. Try again.";
const REDEEM_FAILED = "Could not connect this shop. Try again.";

/**
 * The invitation link's landing. A visitor signs in first (Google or Apple);
 * once the session is a registered merchant with a loaded profile the
 * invitation is previewed, and connecting redeems it and hands the new shop to
 * the onboarding walkthrough.
 */
export class ShopClaimController extends SubtreeController implements ShopClaimActions {
  readonly state: ShopClaimState;
  #previewStarted = false;

  constructor(
    readonly params: ShopClaimParams,
    readonly deps: ShopClaimDeps,
    readonly routing: ShopClaimRouting,
  ) {
    super();
    this.state = new ShopClaimState(params.token);
    this.subtree.put(ShopClaimState, this.state);
    this.subtree.put(ShopClaimActions, this);

    this.sync(() => {
      const { session, profileRepository } = deps;
      const profile = profileRepository.status;
      const signedIn = session.stage === "signedIn";
      this.state.loading.value =
        session.stage === "restoring" || (signedIn && (profile.kind === "idle" || profile.kind === "loading"));
      this.state.profileFailed.value = signedIn && profile.kind === "failed";
      this.state.registered.value = signedIn && !session.isAnonymous && profile.kind === "loaded";
    }, [deps.session, deps.profileRepository]);

    this.sync(() => {
      if (this.state.registered.value && !this.#previewStarted && params.token !== null) {
        this.#previewStarted = true;
        void this.checkInvitation();
      }
    }, [this.state.registered]);
  }

  continueWithGoogle = () => this.#signIn(() => signInWithGoogle(this.deps));

  continueWithApple = () => this.#signIn(() => signInWithApple(this.deps));

  checkInvitation = async () => {
    const token = this.params.token;
    if (token === null) return;
    if (!CLAIM_TOKEN_PATTERN.test(token)) {
      this.state.flow.value = { step: "preview-error", error: INCOMPLETE_LINK };
      return;
    }
    this.state.flow.value = { step: "checking" };
    try {
      const r = await previewClaim({ ...this.deps, token });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.flow.value = { step: "preview-error", error: claimMessage(r.errors, PREVIEW_FAILED) };
        return;
      }
      this.state.flow.value = { step: "connect", shop: r.value, busy: false };
    } catch (e) {
      if (this.isDisposed) return;
      this.state.flow.value = { step: "preview-error", error: thrownText(e, PREVIEW_FAILED) };
    }
  };

  connect = async () => {
    const flow = this.state.flow.value;
    const token = this.params.token;
    if (flow.step !== "connect" || flow.busy || token === null) return;
    const shop = flow.shop;
    this.state.flow.value = { step: "connect", shop, busy: true };
    try {
      const r = await redeemClaim({ ...this.deps, token });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.flow.value = { step: "connect", shop, busy: false, error: claimMessage(r.errors, REDEEM_FAILED) };
        return;
      }
      this.routing.toClaimedShop(r.value.id);
    } catch (e) {
      if (this.isDisposed) return;
      this.state.flow.value = { step: "connect", shop, busy: false, error: thrownText(e, REDEEM_FAILED) };
    }
  };

  close = () => {
    this.routing.close(this.state.registered.value);
  };

  goToLogin = () => {
    this.routing.goToLogin();
  };

  /** A dismissed popup closes the claim, as it did before: the visitor chose not to continue. */
  async #signIn(fn: () => Promise<Result<void, SignInFailed | SignInCancelled>>): Promise<void> {
    const flow = this.state.flow.value;
    if (flow.step !== "login" || flow.busy) return;
    this.state.flow.value = { step: "login", busy: true };
    const r = await fn();
    if (this.isDisposed || r.ok) return;
    if (hasError(r, SignInCancelled)) {
      this.close();
      return;
    }
    this.state.flow.value = { step: "login", busy: false, error: "Sign-in did not complete. Try again." };
  }
}
